import { useState, useEffect } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Activity, Zap, Clock, TrendingUp, Database, Cpu } from 'lucide-react';

interface PerformanceMetrics {
  avg_response_time_ms: number;
  p95_response_time_ms: number;
  p99_response_time_ms: number;
  total_requests: number;
  error_rate: number;
  db_query_time_ms: number;
}

export default function PerformancePage() {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [timeRange, setTimeRange] = useState(24);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadMetrics = async () => {
    try {
      const client = getSupabaseClient();
      const { data, error } = await client
        .rpc('get_performance_metrics', { time_range_hours: timeRange });

      if (error) throw error;
      if (data) setMetrics(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading metrics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="⚡ Performance Metrics" subtitle="Application performance and response times">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg text-slate-400">Loading performance data...</div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="⚡ Performance Metrics"
      subtitle="Application performance and response times"
      actions={
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
          className="px-4 py-2 bg-slate-800 text-slate-200 border border-slate-700 rounded-lg focus:border-blue-500 focus:outline-none"
        >
          <option value={1}>Last Hour</option>
          <option value={24}>Last 24 Hours</option>
          <option value={168}>Last Week</option>
        </select>
      }
    >
      {metrics && (
        <>
          <StatsGrid cols={6}>
            <StatCard
              label="Avg Response Time"
              value={`${metrics.avg_response_time_ms}ms`}
              icon={Clock}
            />
            <StatCard
              label="P95 Response Time"
              value={`${metrics.p95_response_time_ms}ms`}
              icon={TrendingUp}
            />
            <StatCard
              label="P99 Response Time"
              value={`${metrics.p99_response_time_ms}ms`}
              icon={Zap}
              variant="warning"
            />
            <StatCard
              label="Total Requests"
              value={metrics.total_requests}
              icon={Activity}
            />
            <StatCard
              label="Error Rate"
              value={`${(metrics.error_rate * 100).toFixed(2)}%`}
              icon={Activity}
              variant={metrics.error_rate > 0.01 ? 'critical' : 'success'}
            />
            <StatCard
              label="DB Query Time"
              value={`${metrics.db_query_time_ms}ms`}
              icon={Database}
            />
          </StatsGrid>

          <div className="bg-slate-900 rounded-lg border border-slate-800 p-6 mt-6">
            <h2 className="text-xl font-semibold text-slate-200 mb-4">Performance Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Average Response Time</span>
                <span className={`font-semibold ${metrics.avg_response_time_ms < 200 ? 'text-green-400' : metrics.avg_response_time_ms < 500 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {metrics.avg_response_time_ms}ms
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Error Rate</span>
                <span className={`font-semibold ${metrics.error_rate < 0.01 ? 'text-green-400' : metrics.error_rate < 0.05 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {(metrics.error_rate * 100).toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded">
                <span className="text-slate-300">Database Performance</span>
                <span className={`font-semibold ${metrics.db_query_time_ms < 100 ? 'text-green-400' : metrics.db_query_time_ms < 300 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {metrics.db_query_time_ms}ms
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminPageLayout>
  );
}
